import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, ArrowLeft, ArrowRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { uploadImage } from '../services/api';

const MAX_IMAGES = 5;
const MAX_SIZE_MB = 3;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const ImageUploader = ({ images, onImagesChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    // Check max images limit
    const currentCount = images.filter(img => img).length;
    const remainingSlots = MAX_IMAGES - currentCount;
    
    if (remainingSlots <= 0) {
      toast({
        title: "Límite alcanzado",
        description: `Solo puedes subir un máximo de ${MAX_IMAGES} imágenes`,
        variant: "destructive"
      });
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    
    // Validate files
    const validFiles = filesToUpload.filter(file => {
      if (file.size > MAX_SIZE_BYTES) {
        toast({
          title: "Archivo muy grande",
          description: `${file.name} excede el límite de ${MAX_SIZE_MB} MB`,
          variant: "destructive"
        });
        return false;
      }
      
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Tipo no válido",
          description: `${file.name} no es un formato válido (JPG, PNG, WEBP)`,
          variant: "destructive"
        });
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    const newImages = [...images];
    
    for (const file of validFiles) {
      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        // Use Supabase upload directly
        const result = await uploadImage(file);
        
        if (result.success) {
          // Find first empty slot
          const emptyIndex = newImages.findIndex(img => !img);
          if (emptyIndex !== -1) {
            newImages[emptyIndex] = result.url;
          } else {
            newImages.push(result.url);
          }
          
          toast({
            title: "Imagen subida",
            description: `${file.name} se subió correctamente`,
          });
          
          // Update progress to 100%
          setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast({
          title: "Error al subir",
          description: error.message || `No se pudo subir ${file.name}`,
          variant: "destructive"
        });
      } finally {
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[file.name];
          return updated;
        });
      }
    }
    
    onImagesChange(newImages);
    setUploading(false);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    const newImages = [...images];
    newImages[index] = '';
    onImagesChange(newImages);
    
    toast({
      title: "Imagen eliminada",
      description: "La imagen fue removida de la lista",
    });
  };

  const setAsPrimary = (index) => {
    if (index === 0) return;
    const newImages = [...images];
    const [item] = newImages.splice(index, 1);
    newImages.unshift(item);
    onImagesChange(newImages);
  };

  const moveLeft = (index) => {
    if (index <= 0) return;
    const newImages = [...images];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    onImagesChange(newImages);
  };

  const moveRight = (index) => {
    if (index >= images.length - 1) return;
    const newImages = [...images];
    [newImages[index + 1], newImages[index]] = [newImages[index], newImages[index + 1]];
    onImagesChange(newImages);
  };

  const activeImages = images.filter(img => img);
  const canAddMore = activeImages.length < MAX_IMAGES;

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            {activeImages.length} de {MAX_IMAGES} imágenes
          </p>
          <p className="text-xs text-gray-400">
            Máximo {MAX_SIZE_MB} MB por imagen (JPG, PNG, WEBP)
          </p>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading || !canAddMore}
        />
        
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || !canAddMore}
          className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Subiendo...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Subir imágenes
            </>
          )}
        </Button>
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([filename, progress]) => (
            <div key={filename} className="bg-gray-100 rounded-lg p-2">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600 truncate max-w-[200px]">{filename}</span>
                <span className="text-cyan-600 font-medium">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Preview Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, index) => (
          <div 
            key={index}
            className={`relative aspect-video rounded-lg border-2 overflow-hidden ${
              img ? 'border-cyan-300 bg-cyan-50' : 'border-dashed border-gray-300 bg-gray-50'
            }`}
          >
            {img ? (
              <>
                <img 
                  src={img} 
                  alt={`Imagen ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-2 left-2 flex items-center space-x-2">
                  {index === 0 && (
                    <span className="px-2 py-0.5 bg-cyan-500 text-white text-xs rounded-full">
                      Principal
                    </span>
                  )}
                </div>

                <div className="absolute bottom-2 right-2 flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => moveLeft(index)}
                    disabled={index === 0}
                    className={`p-1 bg-white/80 rounded-md hover:bg-white transition-colors ${index === 0 ? 'opacity-50 pointer-events-none' : ''}`}
                    title="Mover a la izquierda"
                  >
                    <ArrowLeft className="h-4 w-4 text-gray-700" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setAsPrimary(index)}
                    disabled={index === 0}
                    className={`p-1 bg-white/80 rounded-md hover:bg-white transition-colors ${index === 0 ? 'opacity-50 pointer-events-none' : ''}`}
                    title="Establecer como principal"
                  >
                    <Star className="h-4 w-4 text-yellow-500" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveRight(index)}
                    disabled={!images[index + 1]}
                    className={`p-1 bg-white/80 rounded-md hover:bg-white transition-colors ${!images[index + 1] ? 'opacity-50 pointer-events-none' : ''}`}
                    title="Mover a la derecha"
                  >
                    <ArrowRight className="h-4 w-4 text-gray-700" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <ImageIcon className="h-8 w-8 mb-1" />
                <span className="text-xs">Imagen {index + 1}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 flex items-start">
        <span className="mr-1">💡</span>
        Puedes reordenar las imágenes usando las flechas o marcar una imagen como <strong>Principal</strong>. La imagen principal será la mostrada en listados y enlaces.
      </p>
    </div>
  );
};

export default ImageUploader;
